import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import DictationInput from '../components/DictationInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  CheckCircle, XCircle, Clock, MessageSquare, Users, 
  AlertCircle, RefreshCw, Building, User, Shield
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

export default function ConnectionsPage() {
  const { user, axiosAuth } = useAuth();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const api = axiosAuth();

  const isAdmin = ['coordinator', 'admin', 'network_admin'].includes(user.role);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, summaryRes] = await Promise.all([
        api.get('/connections/pending-approval'),
        isAdmin ? api.get('/connections/summary') : Promise.resolve({ data: null })
      ]);
      setPendingRequests(pendingRes.data || []);
      setSummary(summaryRes.data);

      if (isAdmin) {
        const allRes = await api.get('/connections/all');
        setAllRequests(allRes.data || []);
      }
    } catch (err) {
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleReview = async (action) => {
    if (!selectedRequest) return;
    try {
      await api.put(`/connections/${selectedRequest.id}/approve?action=${action}&notes=${encodeURIComponent(reviewNotes)}`);
      toast.success(`Request ${action}`);
      setReviewDialogOpen(false);
      setSelectedRequest(null);
      setReviewNotes('');
      fetchData();
    } catch (err) {
      toast.error('Failed to process request');
    }
  };

  const openReviewDialog = (request) => {
    setSelectedRequest(request);
    setReviewDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_approval': return <Badge className="bg-amber-100 text-amber-700"><Clock size={12} className="mr-1" /> Pending</Badge>;
      case 'approved': return <Badge className="bg-sky-100 text-sky-700"><CheckCircle size={12} className="mr-1" /> Approved</Badge>;
      case 'accepted': return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle size={12} className="mr-1" /> Accepted</Badge>;
      case 'rejected': return <Badge className="bg-red-100 text-red-700"><XCircle size={12} className="mr-1" /> Rejected</Badge>;
      case 'declined': return <Badge className="bg-slate-100 text-slate-600"><XCircle size={12} className="mr-1" /> Declined</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="connections-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Connection Requests
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Review and manage partner connection requests to participants
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2 rounded-full" disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
        </Button>
      </div>

      {/* Summary Stats (Admin Only) */}
      {isAdmin && summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-3">
              <p className="text-xl font-bold">{summary.total}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-3">
              <p className="text-xl font-bold text-amber-600">{summary.pending_approval}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-3">
              <p className="text-xl font-bold text-sky-600">{summary.approved_awaiting_response}</p>
              <p className="text-xs text-muted-foreground">Awaiting Response</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-3">
              <p className="text-xl font-bold text-emerald-600">{summary.accepted}</p>
              <p className="text-xs text-muted-foreground">Accepted</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-3">
              <p className="text-xl font-bold text-red-600">{summary.rejected_by_staff}</p>
              <p className="text-xs text-muted-foreground">Rejected</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-3">
              <p className="text-xl font-bold text-teal-600">{summary.success_rate}%</p>
              <p className="text-xs text-muted-foreground">Success Rate</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="bg-muted/50 rounded-full p-1">
          <TabsTrigger value="pending" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Pending Review ({pendingRequests.length})
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="all" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
              All Requests ({allRequests.length})
            </TabsTrigger>
          )}
        </TabsList>

        {/* Pending Requests */}
        <TabsContent value="pending" className="space-y-4">
          {pendingRequests.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-muted-foreground">No pending requests to review.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingRequests.map(req => (
                <Card key={req.id} className="border-0 shadow-sm rounded-2xl border-l-4 border-l-amber-400">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                            <Building className="text-emerald-600" size={20} />
                          </div>
                          <div>
                            <p className="font-semibold">{req.partner_name}</p>
                            <p className="text-xs text-muted-foreground">Community Partner</p>
                          </div>
                          <span className="text-muted-foreground mx-2">to</span>
                          <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center">
                            <User className="text-sky-600" size={20} />
                          </div>
                          <div>
                            <p className="font-semibold">{req.participant_name}</p>
                            <p className="text-xs text-muted-foreground">Participant</p>
                          </div>
                        </div>
                        
                        <div className="bg-muted/30 p-3 rounded-xl">
                          <p className="text-sm font-medium mb-1">Regarding: {req.need_title}</p>
                          {req.message && <p className="text-sm text-muted-foreground">{req.message}</p>}
                          {req.resource_offered && (
                            <p className="text-sm mt-2">
                              <span className="font-medium">Resource offered:</span> {req.resource_offered}
                            </p>
                          )}
                        </div>

                        <p className="text-xs text-muted-foreground">Submitted {formatDate(req.created_at)}</p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => openReviewDialog(req)}
                          variant="outline"
                          className="rounded-full gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        >
                          <CheckCircle size={14} /> Approve
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => { setSelectedRequest(req); handleReview('rejected'); }}
                          variant="outline"
                          className="rounded-full gap-1 border-red-200 text-red-700 hover:bg-red-50"
                        >
                          <XCircle size={14} /> Reject
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* All Requests (Admin) */}
        {isAdmin && (
          <TabsContent value="all" className="space-y-4">
            {allRequests.length === 0 ? (
              <Card className="border-0 shadow-sm rounded-2xl">
                <CardContent className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No connection requests yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-medium">Partner</th>
                      <th className="text-left py-2 px-3 font-medium">Participant</th>
                      <th className="text-left py-2 px-3 font-medium">Need</th>
                      <th className="text-left py-2 px-3 font-medium">Status</th>
                      <th className="text-left py-2 px-3 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRequests.map(req => (
                      <tr key={req.id} className="border-b hover:bg-muted/30">
                        <td className="py-2.5 px-3 font-medium">{req.partner_name}</td>
                        <td className="py-2.5 px-3">{req.participant_name}</td>
                        <td className="py-2.5 px-3 text-muted-foreground max-w-xs truncate">{req.need_title}</td>
                        <td className="py-2.5 px-3">{getStatusBadge(req.status)}</td>
                        <td className="py-2.5 px-3 text-muted-foreground">{formatDate(req.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Approve Connection Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedRequest && (
              <div className="bg-muted/30 p-3 rounded-xl">
                <p className="text-sm"><strong>{selectedRequest.partner_name}</strong> wants to connect with <strong>{selectedRequest.participant_name}</strong></p>
                <p className="text-sm text-muted-foreground mt-1">Regarding: {selectedRequest.need_title}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Notes (optional)</label>
              <DictationInput 
                value={reviewNotes}
                onChange={(val) => setReviewNotes(val)}
                placeholder="Add any notes about this approval..."
                className="rounded-xl"
                rows={2}
              showCleanup={false} />
            </div>
            <div className="bg-emerald-50 p-3 rounded-xl flex items-start gap-2">
              <Shield className="text-emerald-600 shrink-0 mt-0.5" size={16} />
              <p className="text-sm text-emerald-700">
                Approving this request will notify the participant, who can then choose to accept or decline.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button 
              onClick={() => handleReview('approved')} 
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-2"
            >
              <CheckCircle size={16} /> Approve Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
